import type { InjectionKey, Ref } from 'vue'
import type { Agency } from '../Agency'
import type { AgencyStructureNode } from '../AgencyStructureNode'
import type {
  ChangeAgencyNameRequest,
  ChangeAgencyContactInfoRequest,
  MoveAgencyRequest,
  LinkAgencyToAnotherCompanyRequest,
  ReactivateAgencyRequest
} from '../api-requests/AgencyRequests'

/**
 * Business-logic commands for a single agency. Each command owns its full
 * write cycle — loading toggle, command-id generation, the API call, and the
 * post-mutation refetch of the agency (and the agency-structure tree when an
 * `AgencyStructureProvider` is an ancestor). Views call these instead of
 * issuing API requests and orchestrating refetches themselves.
 */
export interface AgencyCommands {
  changeName(agencyId: string, payload: ChangeAgencyNameRequest): Promise<void>
  changeContactInfo(
    agencyId: string,
    payload: ChangeAgencyContactInfoRequest
  ): Promise<void>
  move(agencyId: string, payload: MoveAgencyRequest): Promise<void>
  linkToCompany(
    agencyId: string,
    payload: LinkAgencyToAnotherCompanyRequest
  ): Promise<void>
  deactivate(agencyId: string): Promise<void>
  reactivate(agencyId: string, payload: ReactivateAgencyRequest): Promise<void>
}

export interface AgencyService {
  agency: Ref<Agency | undefined>
  agencyNode: Ref<AgencyStructureNode | undefined>
  isLoading: Ref<boolean>
  findAgencyById(agencyId?: string): Agency | undefined
  fetchAgency(
    agencyId: string,
    options?: { forceFetch: boolean; subscribeToEvents?: boolean }
  ): Promise<Agency | undefined>
  updateAgency(agencyId: string, agency: Agency): void
  clearData(options?: { unsubscribeFromEvents?: boolean }): void
  commands: AgencyCommands
}

export const agencyKey = Symbol('gs-agency') as InjectionKey<AgencyService>
